// ============================================================================
// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BrandCampaignCreate.jsx
// Название: Brand Campaign Create - with Website URL
// Версия: 5.0.0
// Дата: 06.02.2026
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

// ============================================================================
// КОМПОНЕНТ
// ============================================================================

const BrandCampaignCreate = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: ''
  });

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Введите название кампании');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Форматируем URL если введён без протокола
      let websiteUrl = formData.website_url.trim();
      if (websiteUrl && !websiteUrl.startsWith('http')) {
        websiteUrl = 'https://' + websiteUrl;
      }

      const payload = {
        name: formData.name,
        campaign_type: 'brand_giveaway',
        budget_dar: 10,
        reward_amount: 10,
        description: formData.description || '',
        website_url: websiteUrl || null,
        brand_config: {
          restriction: 'unlimited'
        }
      };

      const response = await api.post('/campaigns', payload);
      const campaignId = response.data?.id || response.id;
      
      navigate(`/business/brand/giveaway/${campaignId}/wizard`);
      
    } catch (err) {
      console.error('Create error:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Ошибка создания');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && formData.name.trim()) {
      handleCreate();
    }
  };
  
  // ==========================================================================
  // STYLES
  // ==========================================================================
  
  const styles = {
    page: {
      padding: '40px 24px',
      maxWidth: 560,
      margin: '0 auto',
      minHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    
    // Card
    card: {
      background: 'white',
      borderRadius: 16,
      padding: 32,
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
    },
    
    // Header
    header: {
      textAlign: 'center',
      marginBottom: 32
    },
    icon: {
      fontSize: 48,
      marginBottom: 12
    },
    title: {
      fontSize: 24,
      fontWeight: 700,
      color: '#1f2937',
      margin: '0 0 8px 0'
    },
    subtitle: {
      fontSize: 14,
      color: '#6b7280',
      margin: 0
    },
    
    // Form
    field: {
      marginBottom: 20
    },
    label: {
      display: 'block',
      fontSize: 14,
      fontWeight: 600,
      color: '#374151',
      marginBottom: 8
    },
    required: {
      color: '#EF4444'
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: 10,
      fontSize: 15,
      boxSizing: 'border-box',
      transition: 'border-color 0.2s',
      outline: 'none'
    },
    inputWithIcon: {
      width: '100%',
      padding: '14px 16px 14px 44px',
      border: '2px solid #e5e7eb',
      borderRadius: 10,
      fontSize: 15,
      boxSizing: 'border-box',
      transition: 'border-color 0.2s',
      outline: 'none'
    },
    inputWrapper: {
      position: 'relative'
    },
    inputIcon: {
      position: 'absolute',
      left: 14,
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: 18,
      pointerEvents: 'none',
      color: '#9ca3af'
    },
    textarea: {
      width: '100%',
      padding: '14px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: 10,
      fontSize: 15,
      boxSizing: 'border-box',
      minHeight: 80,
      resize: 'vertical',
      fontFamily: 'inherit',
      outline: 'none'
    },
    hint: {
      fontSize: 12,
      color: '#9ca3af',
      marginTop: 6
    },
    
    // Error
    error: {
      background: '#FEF2F2',
      color: '#DC2626',
      padding: '12px 16px',
      borderRadius: 10,
      marginBottom: 20,
      fontSize: 14,
      textAlign: 'center'
    },
    
    // Actions
    actions: {
      display: 'flex',
      gap: 12,
      marginTop: 8
    },
    btn: {
      flex: 1,
      padding: '14px 20px',
      borderRadius: 10,
      fontSize: 15,
      fontWeight: 600,
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8
    },
    btnPrimary: {
      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(16,185,129,0.25)'
    },
    btnSecondary: {
      background: '#f3f4f6',
      color: '#374151'
    },
    btnDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    },
    
    // Tips
    tips: {
      marginTop: 24,
      padding: '16px 20px',
      background: '#F0FDF4',
      borderRadius: 10,
      border: '1px solid #BBF7D0'
    },
    tipsTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: '#166534',
      marginBottom: 8
    },
    tipsList: {
      margin: 0,
      paddingLeft: 18,
      fontSize: 13,
      color: '#15803D',
      lineHeight: 1.6
    }
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.icon}>🎁</div>
          <h1 style={styles.title}>Новая кампания</h1>
          <p style={styles.subtitle}>Создайте кампанию для привлечения лидов</p>
        </div>

        {/* Error */}
        {error && <div style={styles.error}>❌ {error}</div>}

        {/* Form */}
        <div style={styles.field}>
          <label style={styles.label}>
            Название кампании <span style={styles.required}>*</span>
          </label>
          <input
            style={styles.input}
            type="text"
            placeholder="Например: Акция Февраль 2026"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            onKeyPress={handleKeyPress}
            onFocus={(e) => e.target.style.borderColor = '#10B981'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            autoFocus
          />
          <div style={styles.hint}>Название видно только вам и будет именем бренда</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>
            🌐 Сайт компании
          </label>
          <div style={styles.inputWrapper}>
            <span style={styles.inputIcon}>🔗</span>
            <input
              style={styles.inputWithIcon}
              type="url"
              placeholder="example.com"
              value={formData.website_url}
              onChange={(e) => setFormData({...formData, website_url: e.target.value})}
              onFocus={(e) => e.target.style.borderColor = '#10B981'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <div style={styles.hint}>Ссылка на сайт — пользователи смогут перейти туда из промокода</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Описание</label>
          <textarea
            style={styles.textarea}
            placeholder="Внутренние заметки (опционально)"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            onFocus={(e) => e.target.style.borderColor = '#10B981'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button 
            style={{...styles.btn, ...styles.btnSecondary}} 
            onClick={() => navigate('/business/brand/campaigns')}
          >
            Отмена
          </button>
          <button
            style={{
              ...styles.btn, 
              ...styles.btnPrimary, 
              ...(loading ? styles.btnDisabled : {})
            }}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? '⏳ Создание...' : 'Создать →'}
          </button>
        </div>

        {/* Tips */}
        <div style={styles.tips}>
          <div style={styles.tipsTitle}>💡 Что дальше:</div>
          <ul style={styles.tipsList}>
            <li>Настройте сумму и количество подарков</li>
            <li>Выберите AI-дизайн для картинок</li>
            <li>Раздайте ссылки или QR-коды клиентам</li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default BrandCampaignCreate;